import { useEffect, useMemo, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import { IndianRupee, ChevronLeft, ChevronRight } from "lucide-react";

export default function RatesPage() {
  const [weight, setWeight] = useState(1);
  const [pin, setPin] = useState("");
  const [urgency, setUrgency] = useState("standard");
  const [rates, setRates] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const fetchRates = async () => {
    const { data } = await api.get("/rates", { params: { weight_kg: weight, destination_pincode: pin, urgency } });
    setRates(data);
    setPage(1);
  };
  useEffect(() => { fetchRates(); /* eslint-disable-next-line */ }, [weight, pin, urgency]);

  const cheapest = rates[0]?.price;
  const fastest = [...rates].sort((a,b)=>a.eta_days - b.eta_days)[0];

  const total = rates.length;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const p = Math.min(page, pageCount);
  const pagedRates = useMemo(() => rates.slice((p - 1) * pageSize, p * pageSize), [rates, p, pageSize]);

  return (
    <div className="p-8 space-y-6">
      <header>
        <div className="overline flex items-center gap-2"><IndianRupee size={12}/> Live rate matrix</div>
        <h1 className="text-4xl font-display mt-1">Courier Rates.</h1>
      </header>

      <div className="border border-border bg-card p-6 grid grid-cols-1 md:grid-cols-4 gap-4">
        <label className="block">
          <div className="overline">Weight (kg)</div>
          <Input data-testid="rates-weight" type="number" step="0.1" value={weight} onChange={(e)=>setWeight(parseFloat(e.target.value)||0)} className="rounded-md border-input font-mono" />
        </label>
        <label className="block">
          <div className="overline">Destination Pincode</div>
          <Input data-testid="rates-pin" value={pin} onChange={(e)=>setPin(e.target.value)} className="rounded-md border-input font-mono" placeholder="e.g. 400001" />
        </label>
        <div className="md:col-span-2">
          <div className="overline">Urgency</div>
          <div className="mt-1 inline-flex border border-border">
            {["standard","express"].map((u) => (
              <button key={u} data-testid={`rates-urg-${u}`} onClick={() => setUrgency(u)}
                className={`px-4 py-2 uppercase tracking-widest text-xs ${urgency===u ? "bg-primary text-primary-foreground" : "bg-transparent"}`}>{u}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {pagedRates.map((r) => {
          const isBest = r.price === cheapest;
          const isFast = fastest?.courier === r.courier;
          return (
            <div key={r.courier} data-testid={`ratecard-${r.courier}`}
              className={`border p-6 bg-card hover-brut ${isBest ? "border-primary shadow-brut-orange" : "border-border"}`}>
              <div className="flex items-center justify-between">
                <div className="font-display text-2xl">{r.courier}</div>
                {isBest && <span className="text-[10px] mono uppercase tracking-widest bg-primary text-primary-foreground px-2 py-0.5">Cheapest</span>}
                {isFast && !isBest && <span className="text-[10px] mono uppercase tracking-widest border border-accent px-2 py-0.5">Fastest</span>}
              </div>
              <div className="mt-6 font-display text-6xl leading-none">₹{r.price}</div>
              <div className="mt-3 flex items-center justify-between mono text-xs text-muted-foreground">
                <div>ETA {r.eta_days} day{r.eta_days>1?"s":""}</div>
                <div className="uppercase tracking-widest">{r.urgency}</div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Pagination footer */}
      <div className="flex items-center justify-between flex-wrap gap-2 text-xs text-muted-foreground" data-testid="rates-pagination">
        <div data-testid="rates-total">
          Total <b className="text-foreground mono">{total}</b>{" "}
          {total > 0 && (
            <>· showing <b className="text-foreground mono">{(p - 1) * pageSize + 1}-{Math.min(p * pageSize, total)}</b></>
          )}
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1">
            Rows
            <select
              data-testid="rates-page-size"
              value={pageSize}
              onChange={(e) => { setPageSize(parseInt(e.target.value)); setPage(1); }}
              className="h-8 rounded-md border border-input bg-background px-2"
            >
              {[10, 25, 50, 100].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
          <button
            onClick={() => setPage((pp) => Math.max(1, pp - 1))}
            disabled={p <= 1}
            data-testid="rates-prev"
            className="border border-input rounded-md h-8 w-8 flex items-center justify-center disabled:opacity-40"
          ><ChevronLeft size={13} /></button>
          <span className="mono">{p} / {pageCount}</span>
          <button
            onClick={() => setPage((pp) => Math.min(pageCount, pp + 1))}
            disabled={p >= pageCount}
            data-testid="rates-next"
            className="border border-input rounded-md h-8 w-8 flex items-center justify-center disabled:opacity-40"
          ><ChevronRight size={13} /></button>
        </div>
      </div>
    </div>
  );
}
